
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Modal,
  CircularProgress,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BASE_URL } from "../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../Utlis/Encryption";
import CustomButton from "../../../components/CustomButton";
import mapicon from "../../../assets/Dashboard/mapicon.svg";  // add this line
import empty from "../../../assets/empty.png"; // optional for no data
import { useTranslation } from "react-i18next";
const CompanyDailyPerformance = ({ dateRange }) => {
  const { company, token } = useSelector((state) => state.auth);
  const companyId = company?.id;
  const decryptedToken = token ? decryptToken(token) : null;
const {t}=useTranslation()
  const [data, setData] = useState(null);
//   const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [tempFilter, setTempFilter] = useState({ startDate: "", endDate: "" });
  const [openModal, setOpenModal] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [openSummaryModal, setOpenSummaryModal] = useState(false);

  const getCurrentMonthRange = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const formatLocal = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    return { startDate: formatLocal(first), endDate: formatLocal(last) };
  };

  const formatDateTime = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const fetchData = async (start, end) => {
    try {
      const res = await fetch(`${BASE_URL}/public/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "company_daily",
          startDate: start,
          endDate: end,
          companyId,
          showChartData: true,
        }),
      });
      const result = await res.json();
      if (!result.error && result.data?.data?.length > 0) {
        const formatted = result.data.data.map((r) => ({
          date: new Date(r.report_date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
          }),
          compliance: parseFloat(r.compliance_percentage) || 0,
          productivity: parseFloat(r.productivity_percentage) || 0,
          efficiency: parseFloat(r.efficiency_percentage) || 0,
        }));
        setData(formatted);
      } else setData([]);
    } catch (e) {
      console.error("Error fetching daily performance:", e);
    }
  };

useEffect(() => {
  if (!companyId) return;
  if (dateRange.startDate && dateRange.endDate) {
    fetchData(dateRange.startDate, dateRange.endDate);
  }
}, [companyId]);

// When parent date range changes, re-fetch
useEffect(() => {
  if (companyId && dateRange.startDate && dateRange.endDate) {
    fetchData(dateRange.startDate, dateRange.endDate);
  }
}, [dateRange]);
  if (!data) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "70vh",
        }}
      >
        <CircularProgress size={45} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "18px",
              color: "#003149",
              fontFamily: "Poppins, sans-serif",
            }}
          >
{t("reportsScreen.dailyTitle")}          </Typography>
        
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              border: "1px solid #C4C4C4",
              borderRadius: 1,
              px: 2,
              py: 0.8,
              fontSize: 14,
              bgcolor: "#F9FAFB",
              color: "#003149",
              textAlign: "center",
              minWidth: "260px",
            }}
          >
            {`${formatDateTime(dateRange.startDate)} – ${formatDateTime(
              dateRange.endDate
            )}`}
          </Box>

        </Box>
      </Box>

      {/* Chart */}
      <Card
        sx={{
          p: 2,
          border: "2px solid #091E4224",
          borderRadius: 2,
          boxShadow: "none",
        }}
      >
        {data.length > 0 ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "15px",
                    color: "#44546F",
                    fontWeight: 500,
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  {t("reportsScreen.company_task_day_productivity_efficiency_report")}
                </Typography>
                 <IconButton onClick={() => setOpenSummaryModal(true)}>
            <img src={mapicon} alt="map" style={{ width: 33, height: 33 }} />
          </IconButton>
              </Box>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="compliance"
                stroke="#42A5F5"
                strokeWidth={2}
                dot={false}
                name={t("reportsScreen.compliance")}
              />
              <Line
                type="monotone"
                dataKey="productivity"
                stroke="#66BB6A"
                strokeWidth={2}
                dot={false}
                name={t("reportsScreen.productivity")}
              />
              <Line
                type="monotone"
                dataKey="efficiency"
                stroke="#FFA726"
                strokeWidth={2}
                dot={false}
                name={t("reportsScreen.efficiency")}
              />
            </LineChart>
          </ResponsiveContainer>
          </>
        ) : (
          <Typography
            sx={{
              textAlign: "center",
              py: 10,
              color: "#777",
              fontFamily: "Poppins, sans-serif",
            }}
          >
           {t("reportsScreen.noDataSelectedRange")}
          </Typography>
        )}
      </Card>

   
<Modal open={openSummaryModal} onClose={() => setOpenSummaryModal(false)}>
  <Box
     sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      bgcolor: "white",
      boxShadow: 24,
      p: 3,
      borderRadius: 3,
      width: 500,
      maxHeight: "80vh",
      overflowY: "auto",
      scrollbarWidth: "none", // for Firefox
      msOverflowStyle: "none", // for IE/Edge
      "&::-webkit-scrollbar": {
        display: "none", // for Chrome/Safari
      },
    }}
  >
    {/* Header */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: "#003149",
          fontFamily: "Poppins, sans-serif",
        }}
      >
      {t("reportsScreen.summaryData")}
      </Typography>
      <IconButton onClick={() => setOpenSummaryModal(false)}>
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Table */}
    <Box>
      {data.length > 0 ? (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: "Poppins, sans-serif",
            fontSize: "14px",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <thead>
            <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
              <th style={{ padding: "10px" }}>{t("reportsScreen.date")}</th>
              <th style={{ padding: "10px", textAlign: "right" }}>
        {t("reportsScreen.compliance")}
              </th>
              <th style={{ padding: "10px", textAlign: "right" }}>
{t("reportsScreen.productivity")}              </th>
              <th style={{ padding: "10px", textAlign: "right" }}>
{t("reportsScreen.efficiency")}              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={i}>
                <td
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  {d.date}
                </td>
                <td
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #eee",
                    textAlign: "right",
                  }}
                >
                  {d.compliance}%
                </td>
                <td
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #eee",
                    textAlign: "right",
                  }}
                >
                  {d.productivity}%
                </td>
                <td
                  style={{
                    padding: "8px 10px",
                    borderBottom: "1px solid #eee",
                    textAlign: "right",
                  }}
                >
                  {d.efficiency}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Typography>{t("reportsScreen.noData")}</Typography>
      )}
    </Box>
  </Box>
</Modal>

    </Box>
  );
};

export default CompanyDailyPerformance;
